import { useState } from 'react';
import { Icons } from '../Icons';
import { useLanguage } from '../../contexts/LanguageContext';

const severityColors = {
  critical: 'bg-red-600/20 text-red-400 border-red-500/30',
  high: 'bg-red-500/20 text-red-400 border-red-500/30',
  medium: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  low: 'bg-blue-500/20 text-blue-400 border-blue-500/30'
};

const severityLabels = {
  critical: { en: 'Critical', cs: 'Kritická' },
  high: { en: 'High', cs: 'Vysoká' },
  medium: { en: 'Medium', cs: 'Střední' },
  low: { en: 'Low', cs: 'Nízká' }
};

const phaseColors = {
  dns: '#f59e0b',
  tcp: '#10b981',
  tls: '#6366f1'
};

export const RTTDiagnosticsPanel = ({ url, user }) => {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [multiPageLoading, setMultiPageLoading] = useState(false);
  const [multiPageResults, setMultiPageResults] = useState(null);
  const [expandedPages, setExpandedPages] = useState({});
  
  const hasDeepAccess = user?.tier === 'premium' || user?.tier === 'enterprise' || user?.is_admin;

  const runDiagnostics = async () => {
    if (!url || !user) return;
    
    if (!hasDeepAccess) {
      setShowUpgradeModal(true);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch('/api/lab-audits/rtt-diagnostics', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ url })
      });
      
      const data = await response.json();
      if (data.upgrade_required) {
        setShowUpgradeModal(true);
        return;
      }
      if (data.success && data.data) {
        setResults(data.data);
      } else {
        setError(data.error || 'Analysis failed');
      }
    } catch (err) {
      console.error('RTT diagnostics error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const runMultiPageAnalysis = async () => {
    if (!url || !user) return;
    
    if (!hasDeepAccess) {
      setShowUpgradeModal(true);
      return;
    }
    
    setMultiPageLoading(true);
    
    try {
      const response = await fetch('/api/lab-audits/multi-page-rtt', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ url })
      });
      
      const data = await response.json();
      if (data.upgrade_required) {
        setShowUpgradeModal(true);
        return;
      }
      if (data.success) {
        setMultiPageResults(data);
      }
    } catch (err) {
      console.error('Multi-page RTT error:', err);
    } finally {
      setMultiPageLoading(false);
    }
  };

  const resetResults = () => {
    setResults(null);
    setError(null);
    setMultiPageResults(null);
    setExpandedPages({});
  };

  const togglePageExpanded = (idx) => {
    setExpandedPages(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  const formatMs = (ms) => {
    if (!ms || ms < 0) return '0ms';
    if (ms >= 1000) return `${(ms / 1000).toFixed(2)}s`;
    return `${Math.round(ms)}ms`;
  };

  const getRTTStatus = (rtt) => {
    if (rtt <= 100) return { status: 'good', color: 'text-green-400', bg: 'bg-green-500/20' };
    if (rtt <= 200) return { status: 'needs-improvement', color: 'text-amber-400', bg: 'bg-amber-500/20' };
    return { status: 'poor', color: 'text-red-400', bg: 'bg-red-500/20' };
  };

  const renderMultiPageSection = () => {
    if (!hasDeepAccess) return null;

    return (
      <div className="pt-4 border-t border-slate-700">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs uppercase tracking-wider text-purple-400 flex items-center gap-2">
            <Icons.Layers size={14} />
            {language === 'cs' ? 'Multi-page RTT analýza' : 'Multi-page RTT Analysis'}
          </div>
        </div>

        {!multiPageResults ? (
          <button
            onClick={runMultiPageAnalysis}
            disabled={multiPageLoading}
            className="w-full py-2 px-3 rounded-lg bg-purple-600/20 hover:bg-purple-600/30 border border-purple-500/30 text-purple-400 text-sm flex items-center justify-center gap-2 transition-all disabled:opacity-50"
          >
            {multiPageLoading ? (
              <>
                <Icons.Loader size={14} className="animate-spin" />
                {language === 'cs' ? 'Analyzuji více stránek...' : 'Analyzing multiple pages...'}
              </>
            ) : (
              <>
                <Icons.Layers size={14} />
                {language === 'cs' ? 'Analyzovat RTT napříč webem (až 10 stránek)' : 'Analyze RTT across site (up to 10 pages)'}
              </>
            )}
          </button>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <div className="p-2 rounded-lg bg-slate-800/50 text-center">
                <div className="text-lg font-bold text-purple-400">{formatMs(multiPageResults.summary?.avgRTT)}</div>
                <div className="text-xs text-slate-400">{language === 'cs' ? 'Průměr' : 'Average'}</div>
              </div>
              <div className="p-2 rounded-lg bg-slate-800/50 text-center">
                <div className="text-lg font-bold text-green-400">{formatMs(multiPageResults.summary?.minRTT)}</div>
                <div className="text-xs text-slate-400">{language === 'cs' ? 'Minimum' : 'Best'}</div>
              </div>
              <div className="p-2 rounded-lg bg-slate-800/50 text-center">
                <div className="text-lg font-bold text-red-400">{formatMs(multiPageResults.summary?.maxRTT)}</div>
                <div className="text-xs text-slate-400">{language === 'cs' ? 'Maximum' : 'Worst'}</div>
              </div>
            </div>

            <div className="text-xs text-slate-400">
              {language === 'cs' 
                ? `Analyzováno ${multiPageResults.pagesAnalyzed} stránek`
                : `Analyzed ${multiPageResults.pagesAnalyzed} pages`}
            </div>

            {multiPageResults.summary?.worstPages?.length > 0 && (
              <div className="space-y-2">
                <div className="text-xs text-slate-500">
                  {language === 'cs' ? 'Nejpomalejší stránky:' : 'Slowest pages:'}
                </div>
                {multiPageResults.summary.worstPages.map((page, idx) => (
                  <div key={idx} className="p-2 rounded-lg bg-slate-800/30 border border-slate-700/50">
                    <div 
                      className="flex items-center justify-between cursor-pointer"
                      onClick={() => togglePageExpanded(idx)}
                    >
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <span className={`text-sm font-bold ${getRTTStatus(page.rtt).color}`}>
                          {formatMs(page.rtt)}
                        </span>
                        <span className="text-xs text-slate-400 break-all" title={page.url}>
                          {page.url}
                        </span>
                        {page.cdn && (
                          <span className="text-xs px-1.5 py-0.5 rounded bg-green-500/20 text-green-400">
                            {page.cdn}
                          </span>
                        )}
                      </div>
                      <Icons.ChevronDown 
                        size={14} 
                        className={`text-slate-400 transition-transform ${expandedPages[idx] ? 'rotate-180' : ''}`} 
                      />
                    </div>
                    
                    {expandedPages[idx] && (
                      <div className="mt-2 pt-2 border-t border-slate-700/50">
                        {page.phases && (
                          <div className="flex flex-wrap gap-2 text-xs mb-2">
                            {Object.entries(page.phases).map(([phase, time]) => (
                              <div key={phase} className="flex items-center gap-1">
                                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: phaseColors[phase] }} />
                                <span className="text-slate-400">{phase.toUpperCase()}:</span>
                                <span className="text-white">{formatMs(time)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        {page.causes?.length > 0 && (
                          <div className="space-y-1">
                            {page.causes.map((cause, cIdx) => (
                              <div key={cIdx} className="flex items-center gap-2 text-xs">
                                <span className={`w-1.5 h-1.5 rounded-full ${severityColors[cause.severity]?.split(' ')[0] || 'bg-slate-500'}`} />
                                <span className="text-slate-300">
                                  {language === 'cs' ? cause.description_cs : cause.description_en}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  if (showUpgradeModal) {
    return (
      <div className="bg-slate-800/50 rounded-xl border border-purple-500/30 p-6">
        <div className="text-center">
          <Icons.Lock className="w-12 h-12 text-purple-400 mx-auto mb-4" />
          <h3 className="text-lg font-bold text-white mb-2">
            {language === 'cs' ? 'Premium funkce' : 'Premium Feature'}
          </h3>
          <p className="text-slate-400 mb-4">
            {language === 'cs' 
              ? 'Hloubková RTT diagnostika je dostupná pro Premium a Enterprise uživatele.'
              : 'Deep RTT diagnostics is available for Premium and Enterprise users.'}
          </p>
          <button
            onClick={() => setShowUpgradeModal(false)}
            className="px-4 py-2 bg-slate-700 rounded-lg text-slate-300 hover:bg-slate-600"
          >
            {language === 'cs' ? 'Zavřít' : 'Close'}
          </button>
        </div>
      </div>
    );
  }

  if (!results) {
    return (
      <div className="bg-slate-800/50 rounded-xl border border-cyan-500/30 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-cyan-500/20 rounded-lg">
              <Icons.Zap className="w-5 h-5 text-cyan-400" />
            </div>
            <div>
              <h3 className="font-bold text-white">
                {language === 'cs' ? 'RTT Diagnostika' : 'RTT Diagnostics'}
              </h3>
              <p className="text-sm text-slate-400">
                {language === 'cs' ? 'Analýza síťové latence' : 'Network latency analysis'}
              </p>
            </div>
          </div>
        </div>
        
        {error && (
          <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 text-sm">
            {error}
          </div>
        )}
        
        <button
          onClick={runDiagnostics}
          disabled={loading || !url}
          className="w-full py-3 bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-500/40 rounded-lg text-cyan-400 font-medium transition-all flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {loading ? (
            <>
              <Icons.Loader className="w-5 h-5 animate-spin" />
              {language === 'cs' ? 'Analyzuji...' : 'Analyzing...'}
            </>
          ) : (
            <>
              <Icons.Search className="w-5 h-5" />
              {language === 'cs' ? 'Spustit RTT diagnostiku' : 'Run RTT Diagnostics'}
            </>
          )}
        </button>
      </div>
    );
  }

  const { rtt, phases, causes, recommendations, cdn, protocol } = results;
  const status = getRTTStatus(rtt);
  const totalPhaseTime = Object.values(phases).reduce((a, b) => a + b, 0);

  return (
    <div className="bg-slate-800/50 rounded-xl border border-cyan-500/30 p-6 space-y-6">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <div className={`p-3 rounded-xl flex-shrink-0 ${status.bg}`}>
            <Icons.Zap className={`w-6 h-6 ${status.color}`} />
          </div>
          <div className="min-w-0">
            <h3 className="font-bold text-white text-lg">RTT Diagnostics</h3>
            <p className="text-sm text-slate-400">
              {language === 'cs' ? 'Síťová latence' : 'Network latency'}
            </p>
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <div className={`text-2xl sm:text-3xl font-bold ${status.color}`}>
            {formatMs(rtt)}
          </div>
          <div className="text-xs sm:text-sm text-slate-500">Round-Trip Time</div>
        </div>
      </div>

      <div className="space-y-3">
        <h4 className="text-sm font-medium text-slate-400">
          {language === 'cs' ? 'Rozložení latence' : 'Latency Breakdown'}
        </h4>
        
        <div className="h-8 bg-slate-900 rounded-lg overflow-hidden flex">
          {Object.entries(phases).map(([phase, time]) => {
            if (time <= 0) return null;
            const width = (time / totalPhaseTime) * 100;
            return (
              <div
                key={phase}
                className="h-full flex items-center justify-center text-xs font-medium text-white"
                style={{ 
                  width: `${Math.max(width, 10)}%`, 
                  backgroundColor: phaseColors[phase],
                  minWidth: time > 0 ? '40px' : '0'
                }}
                title={`${phase.toUpperCase()}: ${formatMs(time)}`}
              >
                {width > 15 ? formatMs(time) : ''}
              </div>
            );
          })}
        </div>

        <div className="flex flex-wrap gap-4 text-sm">
          {Object.entries(phases).map(([phase, time]) => (
            <div key={phase} className="flex items-center gap-2">
              <div 
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: phaseColors[phase] }}
              />
              <span className="text-slate-400">{phase.toUpperCase()}:</span>
              <span className="text-white font-medium">{formatMs(time)}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="bg-slate-900/50 rounded-lg p-4">
          <div className="text-sm text-slate-400 mb-1">CDN</div>
          <div className={`font-bold ${cdn ? 'text-green-400' : 'text-amber-400'}`}>
            {cdn || (language === 'cs' ? 'Nezjištěno' : 'Not detected')}
          </div>
        </div>
        <div className="bg-slate-900/50 rounded-lg p-4">
          <div className="text-sm text-slate-400 mb-1">Protocol</div>
          <div className="text-white font-bold">{protocol || 'Unknown'}</div>
        </div>
      </div>

      {causes && causes.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-slate-400">
            {language === 'cs' ? 'Zjištěné problémy' : 'Detected Issues'}
          </h4>
          <div className="space-y-2">
            {causes.map((cause, idx) => (
              <div
                key={idx}
                className={`p-3 rounded-lg border ${severityColors[cause.severity] || severityColors.medium}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium">
                    {language === 'cs' ? cause.description_cs : cause.description_en}
                  </span>
                  <span className="text-xs px-2 py-1 rounded bg-slate-800/50">
                    {severityLabels[cause.severity]?.[language] || cause.severity}
                  </span>
                </div>
                <p className="text-sm opacity-80">
                  {language === 'cs' ? cause.details_cs : cause.details_en}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {recommendations && recommendations.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-slate-400">
            {language === 'cs' ? 'Doporučení' : 'Recommendations'}
          </h4>
          <div className="space-y-2">
            {recommendations.map((rec, idx) => (
              <div key={idx} className="p-3 bg-slate-900/50 rounded-lg border border-slate-700">
                <div className="flex items-center gap-2 mb-1">
                  <Icons.Lightbulb className="w-4 h-4 text-amber-400" />
                  <span className="font-medium text-white">
                    {language === 'cs' ? rec.title_cs : rec.title_en}
                  </span>
                </div>
                <p className="text-sm text-slate-400">
                  {language === 'cs' ? rec.description_cs : rec.description_en}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {renderMultiPageSection()}

      <button
        onClick={resetResults}
        className="w-full py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-slate-300 text-sm transition-colors"
      >
        {language === 'cs' ? 'Spustit novou analýzu' : 'Run new analysis'}
      </button>
    </div>
  );
};

export default RTTDiagnosticsPanel;
